import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  Form,
  InputGroup,
  Modal,
  Container,
  Row,
  Col,
  Spinner,
  Alert,
} from 'react-bootstrap';
import {
  Search,
  Plus,
  Tent,
  Edit,
  Trash2,
  List,
  ShieldCheck,
  Receipt,
  Calendar,
  ChevronDown,
  ChevronRight,
  X as XIcon,
} from 'lucide-react';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import InteractiveTodoList from './InteractiveTodoList';
import LagerBudget from './LagerBudget';
import Sicherheitskonzept from './Sicherheitskonzept';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const calcLagerTage = (startDate, endDate) => {
  const sd = new Date(startDate);
  const ed = new Date(endDate);
  return Math.ceil((ed - sd) / (1000 * 60 * 60 * 24)) + 1;
};

const LagerAdmin = () => {
  const [lager, setLager] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showTodoListModal, setShowTodoListModal] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [showSikoModal, setShowSikoModal] = useState(false);
  const [selectedLager, setSelectedLager] = useState(null);
  const [search, setSearch] = useState('');
  const [editingLager, setEditingLager] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newLager, setNewLager] = useState({
    name: '',
    startDate: '',
    endDate: '',
    lagerleitung: '',
  });
  const [expandedYears, setExpandedYears] = useState({});
  const currentYear = new Date().getFullYear();
  const [showMobileSearch, setShowMobileSearch] = useState(false);

  const fetchLager = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const lagerCollection = collection(db, 'lager');
      const lagerSnapshot = await getDocs(lagerCollection);
      const lagerList = lagerSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLager(lagerList);
    } catch (err) {
      console.error('Fehler beim Laden der Lager:', err);
      setError('Lager konnten nicht geladen werden');
      toast.error('Fehler beim Laden der Lager');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLager();
  }, [fetchLager]);

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingLager(null);
    setNewLager({ name: '', startDate: '', endDate: '', lagerleitung: '' });
  };

  const handleOpenTodoList = useCallback((lagerItem) => {
    setSelectedLager(lagerItem);
    setShowTodoListModal(true);
  }, []);

  const handleOpenBudget = useCallback((lagerItem) => {
    setSelectedLager(lagerItem);
    setShowBudgetModal(true);
  }, []);

  const handleEdit = useCallback((lagerItem) => {
    setEditingLager(lagerItem);
    setNewLager({ ...lagerItem });
    setShowModal(true);
  }, []);

  const handleOpenSiko = useCallback((lagerItem) => {
    setSelectedLager(lagerItem);
    setShowSikoModal(true);
  }, []);

  const handleDelete = useCallback(async (id) => {
    if (window.confirm('Moechten Sie dieses Lager wirklich loeschen?')) {
      try {
        await deleteDoc(doc(db, 'lager', id));
        setLager(prev => prev.filter(l => l.id !== id));
        toast.success('Lager erfolgreich geloescht');
      } catch (error) {
        console.error('Fehler beim Loeschen:', error);
        toast.error('Fehler beim Loeschen des Lagers');
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLager) {
        const lagerDoc = doc(db, 'lager', editingLager.id);
        await updateDoc(lagerDoc, newLager);
        setLager(prev =>
          prev.map(l =>
            l.id === editingLager.id ? { ...newLager, id: editingLager.id } : l
          )
        );
        toast.success('Lager erfolgreich aktualisiert');
      } else {
        const docRef = await addDoc(collection(db, 'lager'), newLager);
        setLager(prev => [...prev, { ...newLager, id: docRef.id }]);
        toast.success('Lager erfolgreich erstellt');
      }
      handleCloseModal();
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      toast.error('Fehler beim Speichern des Lagers');
    }
  };

  const getSeasonColors = useCallback((date) => {
    const month = new Date(date).getMonth();
    if (month >= 2 && month <= 4) {
      return {
        gradient: 'linear-gradient(to right, #f0fff4 0%, #dcfce7 100%)',
        border: '#bbf7d0'
      };
    } else if (month >= 5 && month <= 7) {
      return {
        gradient: 'linear-gradient(to right, #fff7ed 0%, #ffedd5 100%)',
        border: '#fed7aa'
      };
    } else if (month >= 8 && month <= 10) {
      return {
        gradient: 'linear-gradient(to right, #fff7f7 0%, #fee2e2 100%)',
        border: '#fecaca'
      };
    } else {
      return {
        gradient: 'linear-gradient(to right, #f0f9ff 0%, #e0f2fe 100%)',
        border: '#bae6fd'
      };
    }
  }, []);

  const renderLagerCard = useCallback((lagerItem) => {
    const colors = getSeasonColors(lagerItem.startDate);
    return (
      <Card
        className="h-100 shadow-sm hover-shadow"
        style={{
          transition: 'all 0.3s ease',
          backgroundColor: '#ffffff',
          border: 'none',
          borderRadius: '12px',
          overflow: 'hidden'
        }}
      >
        <Card.Body className="d-flex flex-column p-0">
          <div
            className="p-3"
            style={{
              background: colors.gradient,
              borderBottom: `1px solid ${colors.border}`
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div
                className="me-3 d-flex align-items-center justify-content-center"
                style={{
                  backgroundColor: '#ffffff',
                  padding: '10px',
                  borderRadius: '10px',
                  width: '42px',
                  height: '42px',
                  flexShrink: 0,
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                }}
              >
                <Tent size={20} style={{ color: '#0369a1' }} strokeWidth={1.5} />
              </div>
              <div className="d-flex gap-1">
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => handleEdit(lagerItem)}
                  className="p-1 d-flex align-items-center justify-content-center"
                  style={{
                    color: '#64748b',
                    width: '28px',
                    height: '28px',
                    minWidth: '28px',
                    borderRadius: '6px',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  }}
                  title="Bearbeiten"
                >
                  <Edit size={14} />
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => handleDelete(lagerItem.id)}
                  className="p-1 d-flex align-items-center justify-content-center"
                  style={{
                    color: '#64748b',
                    width: '28px',
                    height: '28px',
                    minWidth: '28px',
                    borderRadius: '6px',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  }}
                  title="Loeschen"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h3
                style={{
                  fontWeight: '600',
                  color: '#334155',
                  fontSize: '0.95rem',
                  margin: 0,
                  lineHeight: '1.3'
                }}
              >
                {lagerItem.name}
              </h3>
              <div
                style={{
                  fontSize: '0.85rem',
                  color: '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  padding: '4px 12px',
                  borderRadius: '100px'
                }}
              >
                <Calendar size={13} />
                {new Date(lagerItem.startDate).toLocaleDateString()} - {new Date(lagerItem.endDate).toLocaleDateString()}
              </div>
            </div>
          </div>
          <div className="p-3" style={{ backgroundColor: '#ffffff' }}>
            <div className="mb-3">
              <div
                style={{
                  fontSize: '0.85rem',
                  color: '#475569',
                  backgroundColor: '#f8fafc',
                  padding: '6px 12px',
                  borderRadius: '100px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  border: '1px solid #f1f5f9'
                }}
              >
                <span>Lagerleitung:</span>
                <span style={{ color: '#0369a1', fontWeight: '500' }}>
                  {lagerItem.lagerleitung}
                </span>
              </div>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="light"
                size="sm"
                onClick={() => handleOpenTodoList(lagerItem)}
                className="d-inline-flex align-items-center justify-content-center gap-1 flex-grow-1"
                style={{
                  fontSize: '0.85rem',
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #e0f2fe',
                  color: '#0369a1',
                  padding: '8px',
                  borderRadius: '8px'
                }}
              >
                <List size={14} />
                Todo
              </Button>
              <Button
                variant="light"
                size="sm"
                onClick={() => handleOpenSiko(lagerItem)}
                className="d-inline-flex align-items-center justify-content-center gap-1 flex-grow-1"
                style={{
                  fontSize: '0.85rem',
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #dcfce7',
                  color: '#16a34a',
                  padding: '8px',
                  borderRadius: '8px'
                }}
              >
                <ShieldCheck size={14} />
                SiKo
              </Button>
              <Button
                variant="light"
                size="sm"
                onClick={() => handleOpenBudget(lagerItem)}
                className="d-inline-flex align-items-center justify-content-center gap-1 flex-grow-1"
                style={{
                  fontSize: '0.85rem',
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #e0f2fe',
                  color: '#0284c7',
                  padding: '8px',
                  borderRadius: '8px'
                }}
              >
                <Receipt size={14} />
                Budget
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }, [getSeasonColors, handleDelete, handleEdit, handleOpenBudget, handleOpenTodoList, handleOpenSiko]);

  const filteredLager = lager.filter(l =>
    l.name.toLowerCase().includes(search.toLowerCase()) ||
    l.lagerleitung.toLowerCase().includes(search.toLowerCase())
  );
  const lagerGroups = filteredLager.reduce((groups, item) => {
    const year = new Date(item.startDate).getFullYear();
    if (!groups[year]) groups[year] = [];
    groups[year].push(item);
    return groups;
  }, {});
  const sortedYears = Object.keys(lagerGroups).sort((a, b) => b - a);
  const toggleYear = (year) => {
    setExpandedYears(prev => ({
      ...prev,
      [year]: !(prev[year] !== undefined ? prev[year] : (Number(year) === currentYear))
    }));
  };

  if (isLoading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Laedt...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-4">
        <Alert variant="danger">
          <Alert.Heading>Fehler</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={() => window.location.reload()}>
            Neu laden
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="p-0">
      <style type="text/css">
        {`
          body {
            background-color: rgb(243, 242, 241) !important;
          }
          .hover-shadow {
            transition: all 0.3s ease !important;
          }
          .hover-shadow:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1) !important;
          }
          .mobile-header {
            background-color: #F3F2F1;
            border-bottom: 1px solid rgba(0,0,0,0.1);
            padding: 1rem;
            margin-bottom: 1rem;
          }
          .mobile-header h2 {
            font-weight: bold;
            margin: 0;
            font-size: 2rem;
            color: #333;
          }
          .mobile-actions {
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }
          .mobile-search-wrapper {
            position: relative;
            display: flex;
            align-items: center;
          }
          .mobile-search-container {
            position: absolute;
            right: 100%;
            overflow: hidden;
            transition: all 0.3s cubic-bezier(0.4,0,0.2,1);
            display: flex;
            align-items: center;
            background: #f8f9fa;
            border-radius: 20px;
            margin-right: 8px;
          }
          .mobile-search-container.expanded {
            width: 200px;
            opacity: 1;
            transform: translateX(0);
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
          .mobile-search-container.collapsed {
            width: 0;
            opacity: 0;
            transform: translateX(20px);
            pointer-events: none;
          }
          .mobile-search-input {
            border: none;
            padding: 0.5rem 1rem;
            width: 100%;
            background: transparent;
            font-size: 0.95rem;
            color: #333;
          }
          .mobile-search-input:focus {
            outline: none;
          }
          .btn-icon {
            background: none;
            border: none;
            width: 40px;
            height: 40px;
            padding: 0;
            border-radius: 20px;
            color: #333;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
            position: relative;
            z-index: 2;
          }
          .btn-icon:hover {
            background-color: rgba(0,0,0,0.05);
            color: #0078d4;
          }
          .btn-icon.active {
            background-color: #f0f0f0;
            color: #0078d4;
          }
          .custom-search-input .input-group-text {
            border-radius: 6px 0 0 6px;
            border: 1px solid #ddd;
            background-color: #f7f7f7;
            transition: background-color 0.2s, border-color 0.2s;
          }
          .custom-search-input .input-group-text:hover {
            background-color: #ededed;
          }
          .custom-search-input .form-control {
            border: 1px solid #ddd;
            border-left: none;
            border-radius: 0 6px 6px 0;
            background-color: #fafafa;
            transition: background-color 0.2s, box-shadow 0.2s, border-color 0.2s;
          }
          .custom-search-input .form-control:focus {
            box-shadow: 0 0 0 2px rgba(0,120,212,0.2);
            outline: none;
            background-color: #fff;
            border-color: #0078d4;
          }
          .btn-new-link {
            background-color: #333;
            color: #fff;
            border: none;
            padding: 0.6rem 1.2rem;
            border-radius: 6px;
            font-size: 1rem;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            white-space: nowrap;
          }
          .btn-new-link:hover {
            background-color: #222;
          }
          .btn-new-link:focus {
            outline: none;
            box-shadow: 0 0 0 2px rgba(0,120,212,0.3);
          }
        `}
      </style>

      <div className="mobile-header d-md-none">
        <div className="d-flex justify-content-between align-items-center">
          <h2>LagerAdmin</h2>
          <div className="mobile-actions">
            <div className="mobile-search-wrapper">
              <div className={`mobile-search-container ${showMobileSearch ? 'expanded' : 'collapsed'}`}>
                <input
                  type="text"
                  className="mobile-search-input"
                  placeholder="Suche..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <button 
                className={`btn-icon ${showMobileSearch ? 'active' : ''}`}
                onClick={() => setShowMobileSearch(!showMobileSearch)}
                aria-label="Suche"
              >
                {showMobileSearch ? <XIcon size={20} /> : <Search size={20} />}
              </button>
            </div>
            <Button className="btn-new-link" onClick={() => setShowModal(true)}>
              <Plus size={16} />
              Neu
            </Button>
          </div>
        </div>
      </div>

      <div className="d-none d-md-block py-4">
        <h2
          className="text-center mb-4"
          style={{
            color: '#333',
            fontWeight: '600',
            fontSize: '2.4rem',
            letterSpacing: '0.5px'
          }}
        >
          LagerAdmin
        </h2>
        <div className="d-flex flex-column flex-md-row gap-2 mb-4 align-items-stretch px-3">
          <InputGroup className="flex-grow-1 custom-search-input">
            <InputGroup.Text>
              <Search size={16} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Suche nach Namen oder Lagerleitung..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
          <Button className="btn-new-link" onClick={() => setShowModal(true)}>
            <Plus size={16} />
            Neues Lager
          </Button>
        </div>
      </div>

      {sortedYears.map(year => {
        const isExpanded = expandedYears[year] !== undefined
          ? expandedYears[year]
          : (Number(year) === currentYear);
        return (
          <div key={year} className="mb-4">
            <div 
              onClick={() => toggleYear(year)} 
              style={{
                cursor: 'pointer',
                background: '#f8f9fa',
                padding: '10px 15px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <h4 style={{ margin: 0 }}>{year}</h4>
              {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </div>
            {isExpanded && (
              <Row xs={1} sm={2} md={3} lg={4} className="g-3 mt-2">
                {lagerGroups[year].map(lagerItem => (
                  <Col key={lagerItem.id}>
                    {renderLagerCard(lagerItem)}
                  </Col>
                ))}
              </Row>
            )}
          </div>
        );
      })}

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingLager ? 'Lager bearbeiten' : 'Neues Lager'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Lagername</Form.Label>
              <Form.Control
                type="text"
                value={newLager.name}
                onChange={(e) =>
                  setNewLager(prev => ({ ...prev, name: e.target.value }))
                }
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Startdatum</Form.Label>
              <Form.Control
                type="date"
                value={newLager.startDate}
                onChange={(e) =>
                  setNewLager(prev => ({ ...prev, startDate: e.target.value }))
                }
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Enddatum</Form.Label>
              <Form.Control
                type="date"
                value={newLager.endDate}
                onChange={(e) =>
                  setNewLager(prev => ({ ...prev, endDate: e.target.value }))
                }
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Lagerleitung</Form.Label>
              <Form.Control
                type="text"
                value={newLager.lagerleitung}
                onChange={(e) =>
                  setNewLager(prev => ({ ...prev, lagerleitung: e.target.value }))
                }
                required
              />
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={handleCloseModal}>
                Abbrechen
              </Button>
              <Button variant="primary" type="submit">
                {editingLager ? 'Speichern' : 'Erstellen'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal
        show={showTodoListModal}
        onHide={() => setShowTodoListModal(false)}
        size="lg"
        fullscreen="md-down"
      >
        <Modal.Header closeButton>
          <Modal.Title>Todo-Liste: {selectedLager?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLager && (
            <InteractiveTodoList
              lagerId={selectedLager.id}
              lagerStartDate={selectedLager.startDate}
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showBudgetModal}
        onHide={() => setShowBudgetModal(false)}
        size="xl"
        fullscreen="lg-down"
      >
        <Modal.Header closeButton>
          <Modal.Title>Budget: {selectedLager?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLager && (
            <LagerBudget
              lagerId={selectedLager.id}
              lagerName={selectedLager.name}
              lagerTage={calcLagerTage(selectedLager.startDate, selectedLager.endDate)}
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showSikoModal}
        onHide={() => setShowSikoModal(false)}
        size="xl"
        fullscreen="lg-down"
      >
        <Modal.Header closeButton>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img src="/logo.png" alt="Logo" style={{ height: '40px', marginRight: '10px' }} />
            <Modal.Title>Sicherheitskonzept: {selectedLager?.name}</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body>
          {selectedLager && (
            <Sicherheitskonzept 
              show={showSikoModal} 
              onClose={() => setShowSikoModal(false)} 
              lager={selectedLager} 
            />
          )}
        </Modal.Body>
      </Modal>

      <ToastContainer position="bottom-right" />
    </Container>
  );
};

export default LagerAdmin;